import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import useRetryFetch from "../hooks/useRetryFetch";
import useAuth from "../hooks/useAuth";
import {
  IdentitySpace,
  listSpaces,
  getActiveSpace,
  getViewMode,
  setViewMode as apiSetViewMode,
  setPrimarySpace as apiSetPrimarySpace,
} from "../api/identitySpaces";

// ─── Types ───────────────────────────────────────────────────────────────────

interface SpaceContextType {
  spaces: IdentitySpace[];
  activeSpace: IdentitySpace | null;
  viewMode: "dashboard" | "spaces";
  isLoading: boolean;
  setActiveSpace: (spaceId: string) => Promise<void>;
  setViewMode: (mode: "dashboard" | "spaces") => Promise<void>;
  refreshSpaces: () => Promise<void>;
  hasSpaces: boolean;
}

// ─── Query Keys ─────────────────────────────────────────────────────────────

const SPACES_KEY = ["identity-spaces"] as const;
const VIEW_MODE_KEY = ["user-view-mode"] as const;

// ─── Context ─────────────────────────────────────────────────────────────────

const SpaceContext = createContext<SpaceContextType | null>(null);

export const useSpaces = () => {
  const context = useContext(SpaceContext);
  if (!context) {
    throw new Error("useSpaces must be used within a SpaceProvider");
  }
  return context;
};

// ─── Provider ────────────────────────────────────────────────────────────────

interface SpaceProviderProps {
  children: React.ReactNode;
}

export const SpaceProvider: React.FC<SpaceProviderProps> = ({ children }) => {
  const retryFetch = useRetryFetch();
  const { auth } = useAuth();
  const queryClient = useQueryClient();
  const isAuthenticated = !!auth?.accessToken;

  // Local viewMode state for instant UI (localStorage-backed)
  const [viewModeLocal, setViewModeLocal] = useState<"dashboard" | "spaces">(
    () => {
      const stored = localStorage.getItem("clary_view_mode");
      return stored === "spaces" ? "spaces" : "dashboard";
    }
  );

  /**
   * Fetch view mode from server — syncs localStorage on success
   */
  const { data: serverViewMode } = useQuery({
    queryKey: [...VIEW_MODE_KEY],
    queryFn: async () => {
      const { mode } = await getViewMode(retryFetch);
      const serverMode = mode === "spaces" ? "spaces" : "dashboard";
      // Sync localStorage with server truth
      localStorage.setItem("clary_view_mode", serverMode);
      setViewModeLocal(serverMode);
      return serverMode as "dashboard" | "spaces";
    },
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });

  /**
   * Fetch all spaces
   */
  const {
    data: spaces = [],
    isLoading: spacesLoading,
  } = useQuery<IdentitySpace[]>({
    queryKey: [...SPACES_KEY],
    queryFn: () => listSpaces(retryFetch),
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  /**
   * Derive the active space from the spaces list
   */
  const activeSpace = useMemo(() => {
    if (spaces.length === 0) return null;
    const primary = spaces.find((s) => s.isPrimary);
    const fallback = spaces.find((s) => s.status === "active");
    return primary || fallback || null;
  }, [spaces]);

  // Use local viewMode (instant) — server data syncs it via the query's onSuccess
  const viewMode = viewModeLocal;

  // Loading: true while spaces haven't loaded yet for authenticated users
  const isLoading = isAuthenticated && spacesLoading;

  /**
   * Switch the active space (sets it as primary on the server)
   */
  const setActiveSpace = useCallback(
    async (spaceId: string) => {
      try {
        await apiSetPrimarySpace(retryFetch, spaceId);

        // Optimistically update the cache
        queryClient.setQueryData<IdentitySpace[]>(
          [...SPACES_KEY],
          (old) =>
            old?.map((s) => ({
              ...s,
              isPrimary: s._id === spaceId,
            })) ?? []
        );

        // Also sync activeSpaceId to server
        await apiSetViewMode(retryFetch, viewMode, spaceId);
      } catch (error) {
        console.error("[SpaceProvider] Failed to set active space:", error);
        // Refetch to get correct state
        queryClient.invalidateQueries({ queryKey: [...SPACES_KEY] });
      }
    },
    [retryFetch, viewMode, queryClient]
  );

  /**
   * Switch between dashboard and spaces view modes
   */
  const setViewMode = useCallback(
    async (mode: "dashboard" | "spaces") => {
      // Update local state immediately for instant UI
      setViewModeLocal(mode);
      localStorage.setItem("clary_view_mode", mode);

      // Update cache
      queryClient.setQueryData([...VIEW_MODE_KEY], mode);

      try {
        await apiSetViewMode(retryFetch, mode, activeSpace?._id);
      } catch (error) {
        console.error("[SpaceProvider] Failed to persist view mode:", error);
        // Local state is already updated, server will catch up
      }
    },
    [retryFetch, activeSpace, queryClient]
  );

  /**
   * Refresh spaces — invalidates cache and triggers refetch
   */
  const refreshSpaces = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: [...SPACES_KEY] });
  }, [queryClient]);

  const value: SpaceContextType = {
    spaces,
    activeSpace,
    viewMode,
    isLoading,
    setActiveSpace,
    setViewMode,
    refreshSpaces,
    hasSpaces: spaces.length > 0,
  };

  return (
    <SpaceContext.Provider value={value}>{children}</SpaceContext.Provider>
  );
};

export default SpaceContext;
